import * as React from "react";
import styled from "styled-components";

import { DateTime } from "luxon";

import { Text } from "../../../../components/atoms";
import { theme } from "../../../../theme";
import { dateFromIso } from "../../../../utils/dateIso";

import { DatePickerWeekdays } from "../weekdays";

interface IProps {
  currentDate: DateTime;
  maxDaysRange?: number;
  onSelectDate(date: DateTime): void;
  selectedDate?: string;
  selectedMonth: DateTime;
}

interface IDateItem {
  isOffRange: boolean;
  isOffSelectedMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
}

const DatePickerDateWrapper = styled.div`
  flex-basis: calc(100% / 7);
  text-align: center;
  vertical-align: middle;
`;

const DatePickerDatesGridLayout = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  border-radius: 20px;
  background: ${theme.color.ruubyNewPinkLight};

  ${DatePickerDateWrapper} {
    &:nth-child(1n + 8) {
      border-top: 0.5px solid ${theme.color.ruubyLightRose};
    }
  }
`;

const DatePickerDate = styled(Text)`
  padding: 5px;
  margin: 5px;
  user-select: none;

  background-color: ${(props: IDateItem) =>
    props.isSelected ? theme.color.ruubyPink : "inherit"};

  color: ${(props: IDateItem) =>
    props.isOffRange
      ? theme.color.borderColor
      : props.isToday
      ? theme.color.ruubyBlack
      : "inherit"};

  border-radius: ${(props: IDateItem) => (props.isSelected ? `50%` : `none`)};

  cursor: ${(props: IDateItem) => (props.isOffRange ? `default` : `pointer`)};

  ${(props: IDateItem) => props.isOffSelectedMonth && `visibility: hidden`}
`;

const DatePickerDatesGridWrapper = styled.div``;

const DatePickerDatesGrid: React.FC<IProps> = (props) => {
  return (
    <DatePickerDatesGridWrapper>
      <DatePickerWeekdays />
      <DatePickerDatesGridLayout>
        {getDays(props.selectedMonth).map((date, index) => (
          <DatePickerDateWrapper key={index}>
            <DatePickerDate
              isSelected={
                !!(
                  props.selectedDate &&
                  date.toISODate() ===
                    dateFromIso(props.selectedDate).toISODate()
                )
              }
              isOffRange={isDayOffMonthRange(date, props.maxDaysRange)}
              isOffSelectedMonth={date.month !== props.selectedMonth.month}
              isToday={date.toISODate() === props.currentDate.toISODate()}
              onClick={() =>
                isDayOffMonthRange(date, props.maxDaysRange)
                  ? undefined
                  : props.onSelectDate(date)
              }
            >
              {date.toFormat("d")}
            </DatePickerDate>
          </DatePickerDateWrapper>
        ))}
      </DatePickerDatesGridLayout>
    </DatePickerDatesGridWrapper>
  );
};

/**
 * Check if day is in range of available selection
 * If no max days range, then exclude only days before today
 */
function isDayOffMonthRange(date: DateTime, maxDaysRange?: number) {
  const currentDayMinus3Hours = -0.88;

  if (maxDaysRange) {
    return (
      date.diffNow("days").days <= currentDayMinus3Hours ||
      date.diffNow("days").days >= maxDaysRange
    );
  } else {
    return date.diffNow("days").days <= currentDayMinus3Hours;
  }
}

/**
 * Calculate days for screen
 */
function getDays(date: DateTime): DateTime[] {
  const daysForScreen = 42;

  return Array(daysForScreen)
    .fill(0)
    .map((_, i) => date.startOf("month").startOf("week").plus({ days: i }));
}

export { DatePickerDatesGrid };
